import { AnimatePresence, motion } from "motion/react"
import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { cn } from "@/lib/utils"
import { HugeiconsIcon } from "@hugeicons/react"
import { AlertCircleIcon, CheckmarkCircle02Icon } from "@hugeicons/core-free-icons"

interface NotificationItem {
  id: string
  variant: "error" | "success"
  title: string
  description?: string
}

// Module-level bus — lets notify() be called from anywhere (no React context needed)
const _listeners = new Set<(items: NotificationItem[]) => void>()
let _items: NotificationItem[] = []

function _emit(items: NotificationItem[]) {
  _items = items
  _listeners.forEach((l) => l(items))
}

export const notify = {
  error: (title: string, opts?: { description?: string; duration?: number }) => {
    const id = crypto.randomUUID()
    _emit([..._items, { id, variant: "error", title, description: opts?.description }])
    setTimeout(() => {
      _emit(_items.filter((n) => n.id !== id))
    }, opts?.duration ?? 4000)
  },
  success: (title: string, opts?: { description?: string; duration?: number }) => {
    const id = crypto.randomUUID()
    _emit([..._items, { id, variant: "success", title, description: opts?.description }])
    setTimeout(() => {
      _emit(_items.filter((n) => n.id !== id))
    }, opts?.duration ?? 3000)
  },
}

export function NotificationProvider() {
  const [items, setItems] = useState<NotificationItem[]>([])

  useEffect(() => {
    const handler = (newItems: NotificationItem[]) => setItems([...newItems])
    _listeners.add(handler)
    return () => { _listeners.delete(handler) }
  }, [])

  return createPortal(
    <div className="fixed inset-x-0 top-4 z-[9999] flex flex-col gap-2 items-center pointer-events-none">
      <AnimatePresence mode="popLayout">
        {items.map((item) => (
          <NotificationCard key={item.id} item={item} />
        ))}
      </AnimatePresence>
    </div>,
    document.body,
  )
}

interface NotificationCardProps {
  item: NotificationItem
}

function NotificationCard({ item }: NotificationCardProps) {
  const isError = item.variant === "error"

  return (
    <motion.figure
      initial={{ opacity: 0, y: -12, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.94, transition: { duration: 0.15 } }}
      transition={{ type: "spring", stiffness: 420, damping: 36 }}
      className={cn(
        "relative flex w-[480px] cursor-default select-none overflow-hidden rounded-2xl px-4 py-3",
        "bg-white [box-shadow:0_0_0_1px_rgba(0,0,0,.06),0_4px_16px_rgba(0,0,0,.1)]",
        "dark:bg-zinc-900 dark:[border:1px_solid_rgba(255,255,255,.08)] dark:[box-shadow:0_-20px_80px_-20px_#ffffff1f_inset] dark:backdrop-blur-md",
        "pointer-events-auto",
      )}
    >
      <div className="flex flex-row items-start gap-3">
        <div className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-xl",
          isError ? "bg-destructive/10" : "bg-green-500/10",
        )}>
          <HugeiconsIcon
            icon={isError ? AlertCircleIcon : CheckmarkCircle02Icon}
            size={16}
            className={isError ? "text-destructive" : "text-green-500"}
          />
        </div>
        <div className="flex flex-col gap-0.5 overflow-hidden flex-1">
          <p className="text-sm font-medium text-foreground leading-snug">{item.title}</p>
          {item.description && (
            <p className="text-xs text-muted-foreground leading-relaxed">{item.description}</p>
          )}
        </div>
      </div>
    </motion.figure>
  )
}
